import { API_BASE_URL } from "../../../../config/api";
import { getToken } from "../../../../shared/data/storage/authStorage";

export interface LutItem {
  id: number | string;
  /** ZOUR-90 — the GST registration this LUT was filed for. NULL = primary. */
  location_id?: string | null;
  /** ZOUR-90 — the GSTIN printed on the document, as extracted at upload. */
  gstin?: string | null;
  arn_number: string | null;
  financial_year: string | null;
  valid_from: string | null;
  valid_to: string | null;
  original_filename: string | null;
  file_size?: number | string | null;
  status: string;
  is_currently_valid?: boolean;
  created_at?: string | null;
}

export interface ExportCustomer {
  customer_name: string;
  gst_treatment: string;
  gstin_vat_tax_id: string | null;
  country: string | null;
  state: string | null;
}

export type LutCurrentStatus = "valid" | "expiring_soon" | "expired" | "missing";

export interface FyOption {
  fy: string;
  start_date: string;
  end_date: string;
  is_next: boolean;
  is_current: boolean;
}

/** Compliance-tab status payload (company-level, current financial year). */
export interface LutSummary {
  current_fy: string;
  current_fy_start: string;
  current_fy_end: string;
  current_status: LutCurrentStatus;
  days_to_expiry: number | null;
  active_lut: LutItem | null;
  history: LutItem[];
  fy_options: FyOption[];
  covered_fys: string[];
  /**
   * ZOUR-90 — coverage per GST registration.
   *
   * An LUT is filed per GSTIN, so a two-registration client needs two. Empty
   * (one row) and `has_multiple` false for a single-registration client, which
   * is what keeps the page identical for them.
   */
  locations?: LutLocationCoverage[];
  has_multiple?: boolean;
  /** Names of active registrations with no valid LUT for today. */
  uncovered?: string[];
  /** One sentence about coverage, or null on a single-registration client. */
  coverage_message?: string | null;
}

/** ZOUR-90 — one registration's LUT position. */
export interface LutLocationCoverage {
  zoho_location_id: string | null;
  name: string;
  gstin: string | null;
  is_primary: boolean;
  covered: boolean;
  active_lut: LutItem | null;
  days_to_expiry: number | null;
}

export interface LutExtracted {
  is_lut_document: boolean;
  /** AI classification: "lut_acknowledgement" | "invoice" | "other". */
  document_type?: string | null;
  arn_number: string | null;
  financial_year: string | null;
  valid_from: string | null;
  valid_to: string | null;
  legal_name: string | null;
  gstin: string | null;
  confidence: string | null;
}

export interface LutExtractResult {
  extracted: LutExtracted;
  company: { name: string; gstin: string | null };
  company_match: {
    status: "match" | "mismatch" | "unknown";
    reason: string;
  };
  /**
   * ZOUR-90 — which of our GST registrations the document's GSTIN belongs to.
   *
   * Null when it matches none of them, which is exactly when the mismatch
   * warning stands and the admin has to pick one.
   */
  matched_location?: {
    zoho_location_id: string | null;
    name: string | null;
    gstin: string | null;
    is_primary: boolean;
  } | null;
}

const authHeaders = (): Record<string, string> => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function listLuts(): Promise<LutItem[]> {
  const res = await fetch(`${API_BASE_URL}/api/lut`, {
    headers: { ...authHeaders() },
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json?.message || "Failed to load LUTs");
  }
  return json.data?.luts ?? [];
}

/** Company-level LUT status + FY options + past-FY history for the tab. */
export async function getLutSummary(): Promise<LutSummary> {
  const res = await fetch(`${API_BASE_URL}/api/lut/summary`, {
    headers: { ...authHeaders() },
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json?.message || "Failed to load LUT status");
  }
  return json.data;
}

/** AI-read the LUT document; advisory only — prefills the upload form. */
export async function extractLut(form: FormData): Promise<LutExtractResult> {
  const res = await fetch(`${API_BASE_URL}/api/lut/extract`, {
    method: "POST",
    headers: { ...authHeaders() }, // do NOT set Content-Type for FormData
    body: form,
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json?.message || "Could not read the document");
  }
  return json.data;
}

export async function uploadLut(form: FormData): Promise<LutItem> {
  const res = await fetch(`${API_BASE_URL}/api/lut/upload`, {
    method: "POST",
    headers: { ...authHeaders() }, // do NOT set Content-Type for FormData
    body: form,
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json?.message || "Upload failed");
  }
  return json.data;
}

/** Upload that also supersedes an older LUT for the same financial year. */
export async function uploadLutV2(form: FormData): Promise<LutItem> {
  const res = await fetch(`${API_BASE_URL}/api/lut/upload-v2`, {
    method: "POST",
    headers: { ...authHeaders() }, // do NOT set Content-Type for FormData
    body: form,
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json?.message || "Upload failed");
  }
  return json.data;
}

export async function deleteLut(id: number | string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/lut/${id}`, {
    method: "DELETE",
    headers: { ...authHeaders() },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) {
    throw new Error(json?.message || "Delete failed");
  }
}

export async function getLutUrl(id: number | string): Promise<string> {
  const res = await fetch(`${API_BASE_URL}/api/lut/${id}/url`, {
    headers: { ...authHeaders() },
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json?.message || "Failed to get file URL");
  }
  return json.data?.url;
}

export async function getExportCustomers(): Promise<ExportCustomer[]> {
  const res = await fetch(`${API_BASE_URL}/api/lut/export-customers`, {
    headers: { ...authHeaders() },
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json?.message || "Failed to load export customers");
  }
  return json.data?.customers ?? [];
}
